
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Home, Save, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import ChartSVG from '../charts/ChartSVG';
import { useChartCopy } from '@/hooks/useChartCopy';

interface ConvergenceDisplayProps {
  convergenceRound: number | null;
  rounds: Array<{
    proximityScore?: number;
    userWord?: string;
    aiWord?: string;
  }>;
  user: any;
  onSignInToSave: () => void;
}

export const ConvergenceDisplay = ({
  convergenceRound,
  rounds,
  user,
  onSignInToSave
}: ConvergenceDisplayProps) => {
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);
  const {
    handleCopyChart,
    pngUrl,
    isMobile
  } = useChartCopy({
    svgRef
  });

  const width = 320;
  const height = 400;
  const padding = 40;
  const lastRound = rounds.length > 0 ? rounds[rounds.length - 1] : null;
  const actualFinalWord = lastRound?.userWord || "";
  const isConverged = lastRound && lastRound.proximityScore === 100;

  return (
    <motion.div 
      className="text-center p-8 bg-gradient-to-r from-gray-800 to-gray-700 dark:from-gray-900 dark:to-gray-800 rounded-lg shadow-lg animate-fadeIn" 
      initial={{
        scale: 0.8,
        opacity: 0
      }} 
      animate={{
        scale: 1,
        opacity: 1
      }} 
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 20,
        duration: 0.6
      }}
    >
      <motion.h2 
        className="text-3xl font-bold text-foreground mb-4" 
        initial={{
          y: -20
        }} 
        animate={{
          y: 0
        }} 
        transition={{
          delay: 0.3,
          duration: 0.5
        }}
      >
        🎉 Convergence!
      </motion.h2>

      <motion.p 
        className="text-xl mb-6 text-muted-foreground" 
        initial={{
          opacity: 0
        }} 
        animate={{
          opacity: 1
        }} 
        transition={{
          delay: 0.6,
          duration: 0.5
        }}
      >
        {isMobile 
          ? "Long-click your game badge below to copy and share" 
          : "Click your game badge below to copy and share"}
      </motion.p>

      <motion.div 
        className="flex justify-center mb-6" 
        initial={{
          opacity: 0
        }} 
        animate={{
          opacity: 1
        }} 
        transition={{
          delay: 0.9,
          duration: 0.5
        }}
      >
        {/* Hidden SVG for reference */}
        <div className="hidden">
          <ChartSVG 
            ref={svgRef} 
            rounds={rounds} 
            width={width} 
            height={height} 
            padding={padding} 
            isConverged={isConverged} 
            gameType="Game" 
            date={new Date().toISOString()} 
            actualFinalWord={actualFinalWord} 
            convergenceRound={convergenceRound} 
          />
        </div>
        
        {/* Chart display */}
        <div className="relative w-[320px] mx-auto">
          {pngUrl ? (
            <>
              <img 
                src={pngUrl} 
                alt="Game results chart" 
                className="w-full h-auto rounded-md border border-gray-700"
              />
              
              {/* Only show clickable overlay on desktop */}
              {!isMobile && (
                <div 
                  className="absolute inset-0 bg-black/10 opacity-0 hover:opacity-100 rounded-md transition-opacity duration-300 flex items-center justify-center cursor-pointer" 
                  onClick={handleCopyChart}
                >
                  <div className="bg-black/70 rounded-full p-2">
                    <Copy className="w-6 h-6 text-white" />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="w-[320px] h-[400px] bg-gray-800 rounded-md flex items-center justify-center">
              <div className="text-muted-foreground">Generating image...</div>
            </div>
          )}
        </div>
      </motion.div>

      <motion.div 
        initial={{
          scale: 0
        }} 
        animate={{
          scale: 1
        }} 
        transition={{
          delay: 1.2,
          type: "spring",
          stiffness: 200
        }} 
        className="chart-actions flex flex-col sm:flex-row gap-3 justify-center items-center"
      >
        <Button 
          size="default" 
          onClick={() => navigate("/")} 
          className="bg-primary/90 hover:bg-primary transition-colors duration-300 flex items-center gap-2 opacity-80"
        >
          <Home className="w-4 h-4" />
          Back home
        </Button>

        {!user && (
          <Button 
            size="default" 
            variant="default" 
            onClick={onSignInToSave} 
            className="bg-primary/90 hover:bg-primary transition-colors duration-300 flex items-center gap-2 opacity-80"
          >
            <Save className="w-4 h-4" />
            Sign in to save your results
          </Button>
        )}
      </motion.div>
    </motion.div>
  );
};
